import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAppStore } from '../store';
import {
  shouldDestroySessionOn401,
  shouldLetBrowserSetContentType,
} from '../auth/accessDecisions';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('helios_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ⚠️ D.129 — ON RETIRE LE Content-Type POUR UN CORPS QUE LE NAVIGATEUR
    // SAIT TYPER. 21 appels ecrivaient `multipart/form-data` a la main, donc
    // sans `boundary` : axios pose les en-tetes verbatim (aucun cas
    // particulier FormData dans son adaptateur xhr), le serveur recevait un
    // corps qu'il ne pouvait pas segmenter, et tous les televersements
    // echouaient.
    //
    // ⚠️ On SUPPRIME, on ne remplace pas : laisser le defaut
    // `application/json` ci-dessus ferait convertir le FormData en JSON par
    // `transformRequest` d'axios, donc perdrait le fichier.
    if (config.headers && shouldLetBrowserSetContentType(config.data)) {
      if (typeof (config.headers as { delete?: (k: string) => void }).delete === 'function') {
        (config.headers as { delete: (k: string) => void }).delete('Content-Type');
      } else {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
        delete (config.headers as Record<string, unknown>)['content-type'];
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: string }>) => {
    if (error.response) {
      const { status } = error.response;

      if (status === 401) {
        // ⚠️ D.1306 — TOUT 401 NE TUE PAS LA SESSION.
        // Sur un point d'entree qui verifie des identifiants, un 401 signifie
        // « mot de passe faux », pas « votre session est morte ». L'ancien code
        // ne faisait pas la difference : un mauvais mot de passe effacait le
        // jeton et faisait `window.location.href = '/login'`, donc un
        // RECHARGEMENT COMPLET — qui emportait l'etat React et avec lui le
        // message « Identifiants incorrects. » de LoginPage. L'utilisateur
        // voyait une page blanche qui se recharge, jamais l'erreur.
        if (shouldDestroySessionOn401(error.config?.url)) {
          localStorage.removeItem('helios_token');
          // Le store doit savoir : l'evenement `storage` d'AuthProvider ne se
          // declenche que dans les AUTRES onglets, jamais dans celui-ci.
          try {
            useAppStore.getState().authLogout();
          } catch (e) {
            console.error('[Helios] authLogout a echoue', e);
          }
          // `replace` et non `href` : une session morte n'a rien a faire dans
          // l'historique. Et on ne renavigue pas si on y est deja — c'est ce
          // qui fabriquait la boucle de rechargement sur /login.
          if (window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
        }
        return Promise.reject(error);
      }

      if (status === 403) {
        console.error('[Helios] Forbidden:', error.response.data?.detail);
      }

      if (status === 429) {
        console.warn('[Helios] Rate limited. Retrying...');
        const retryAfter = error.response.headers['retry-after'];
        const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : 2000;
        return new Promise((resolve) => {
          setTimeout(() => resolve(apiClient(error.config!)), delay);
        });
      }

      if (status >= 500) {
        console.error('[Helios] Server error:', status, error.response.data?.detail);
      }
    } else if (error.request) {
      console.error('[Helios] Network error - no response received');
    }

    return Promise.reject(error);
  }
);

export default apiClient;
